import React from 'react';
import styled from 'styled-components';
import { useRetailSection } from '../../../hooks/useRetailSection';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';

const coverAccidentalDuchess = 'https://BelvoirCastle.b-cdn.net/Books/The-Accidental-Duchess-asset.webp';
const coverCapabilityBrown = 'https://BelvoirCastle.b-cdn.net/Books/brown-book.webp';
const coverThousandYears = 'https://BelvoirCastle.b-cdn.net/Books/thousand-years.webp';
const coverResolution = 'https://BelvoirCastle.b-cdn.net/Books/resolution.webp';
const coverShooting = 'https://BelvoirCastle.b-cdn.net/Books/shoooting.webp';

const banner = 'https://BelvoirCastle.b-cdn.net/Banners/historyBan.webp';

const VENPOS_BASE = 'https://online1.venpos.net/VenposJourneyUI/?LocationId=978&Id=';

const VENPOS = {
  accidentalDuchess: `${VENPOS_BASE}2d9650af-938f-47db-b250-e5aa90b2209a`,
  capabilityBrown: `${VENPOS_BASE}e1f30446-5e27-4c53-bc75-5beac5be1e8e`,
  thousandYears: `${VENPOS_BASE}3d7aeba6-9a77-439c-a189-7f8d8376680f`,
  resolution: `${VENPOS_BASE}25843de0-f00c-4c4a-ba92-161c32b54617`,
  resolutionPaperback: `${VENPOS_BASE}10dbdcda-7d27-4c12-b33d-856836ead94a`,
  shooting: `${VENPOS_BASE}73f19414-3246-422f-a25a-80e72104b956`,
};

/** Shared cover band height so featured block and grid cards line up visually */
const COVER_BAND_MIN = 340;
const COVER_MAX_W = 220;

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const Content = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 2rem 4rem;

  @media (max-width: 768px) {
    padding: 0 1.5rem 3rem;
  }
`;

const Intro = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  line-height: 1.85;
  color: ${props => props.theme.colors.primary};
  text-align: center;
  max-width: 720px;
  margin: 0 auto 3.5rem;
`;

const CoverBand = styled.div`
  background: ${props => props.theme.colors.greyBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${COVER_BAND_MIN}px;
  padding: 1.5rem 1.25rem;
  box-sizing: border-box;
`;

const CoverImage = styled.img`
  display: block;
  width: 100%;
  max-width: ${COVER_MAX_W}px;
  max-height: ${COVER_BAND_MIN - 48}px;
  height: auto;
  object-fit: contain;
  object-position: center center;
  border-radius: 4px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.12);
`;

const Featured = styled.article`
  display: grid;
  grid-template-columns: minmax(240px, 280px) 1fr;
  gap: 0;
  align-items: stretch;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(16, 29, 64, 0.08);
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeaturedBody = styled.div`
  padding: 2rem 2.25rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: left;

  @media (max-width: 768px) {
    text-align: center;
    padding: 2rem 1.5rem;
  }
`;

const BookTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.5rem;
  line-height: 1.2;

  @media (max-width: 576px) {
    font-size: 1.65rem;
  }
`;

const BookSubtitle = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.colors.accent};
  margin: 0 0 1.25rem;
  font-weight: 600;
  letter-spacing: 0.02em;
`;

const BookBody = styled.p`
  font-size: 1.05rem;
  line-height: 1.75;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1.5rem;
  opacity: 0.92;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  align-items: stretch;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.article`
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 32px rgba(16, 29, 64, 0.07);
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const CardBody = styled.div`
  padding: 1.5rem 1.75rem 1.75rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.45rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.75rem;
  line-height: 1.25;
`;

const CardText = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: ${props => props.theme.colors.primary};
  margin: 0;
  flex: 1;
  opacity: 0.9;
`;

const CtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: auto;
  padding-top: 1.25rem;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const FeaturedCtaRow = styled(CtaRow)`
  margin-top: 0;
  padding-top: 0;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const ctaButtonStyles = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 600;
  padding: 0.85rem 1.5rem;
  border-radius: 25px;
  text-decoration: none;
  transition: opacity 0.2s ease, transform 0.2s ease;
  cursor: pointer;
  border: 2px solid transparent;
  box-sizing: border-box;

  &:hover {
    opacity: 0.92;
    transform: translateY(-1px);
  }
`;

const VenposBuyButton = styled.a`
  ${ctaButtonStyles}
  background-color: ${props => props.theme.colors.accent};
  color: #fff;
`;

const BOOKS_GRID = [
  {
    id: 'capability-brown',
    cover: coverCapabilityBrown,
    title: 'Capability Brown & Belvoir',
    subtitle: 'Discovering a Lost Landscape',
    venposUrl: VENPOS.capabilityBrown,
    description:
      'Explore how Lancelot “Capability” Brown shaped the estate and how Belvoir’s lost landscape is being read again through archive, archaeology, and the parkland you see today.',
  },
  {
    id: 'thousand-years',
    cover: coverThousandYears,
    title: 'Belvoir Castle',
    subtitle: 'A Thousand Years of Family Art and Architecture',
    venposUrl: VENPOS.thousandYears,
    description:
      'A richly illustrated journey through the castle’s collections and architecture—the art, rooms, and family story that make Belvoir one of England’s great country houses.',
  },
  {
    id: 'resolution',
    cover: coverResolution,
    title: 'Resolution - Hardback',
    subtitle: 'Hardback · Two Brothers. A Nation in Crisis. A World at War',
    venposUrl: VENPOS.resolution,
    description:
      'Drawn from the archives at Belvoir, this history follows two Manners brothers through political upheaval and war at sea in the age of revolution—a portrait of family and nation.',
    credit: 'David Rutland & Emma Ellis',
  },
  {
    id: 'resolution-paperback',
    cover: coverResolution,
    title: 'Resolution - Softback',
    subtitle: 'Paperback · Two Brothers. A Nation in Crisis. A World at War',
    venposUrl: VENPOS.resolutionPaperback,
    description:
      'Drawn from the archives at Belvoir, this history follows two Manners brothers through political upheaval and war at sea in the age of revolution—a portrait of family and nation.',
    credit: 'David Rutland & Emma Ellis',
  },
  {
    id: 'shooting',
    cover: coverShooting,
    title: 'Shooting: A Season of Discovery',
    venposUrl: VENPOS.shooting,
    description:
      'Follow a season of shooting and discovery on the Belvoir estate—landscape, tradition, and the rhythm of the countryside captured in words and photographs.',
  },
];

const BelvoirBooks = () => {
  useRetailSection();

  return (
    <Page>
      <ParallaxBanner
        title="Belvoir Books"
        subtitle="Stories of the castle, the family, and the landscape—from our shelves to yours"
        image={banner}
      />

      <Content>
        <Intro>
          Discover a hand-picked selection of titles written from or rooted in Belvoir—memoir, art and
          architecture, landscape history, and archival narrative. Order for delivery.
        </Intro>

        <Featured>
          <CoverBand>
            <CoverImage src={coverAccidentalDuchess} alt="Book cover: The Accidental Duchess" />
          </CoverBand>
          <FeaturedBody>
            <BookTitle>The Accidental Duchess</BookTitle>
            <BookSubtitle>Life, love, and Belvoir</BookSubtitle>
            <BookBody>
              An intimate account of becoming Duchess of Rutland and making a home at Belvoir—honest,
              warm, and steeped in the day-to-day reality of a working historic house and family seat.
            </BookBody>
            <FeaturedCtaRow>
              <VenposBuyButton href={VENPOS.accidentalDuchess} target="_blank" rel="noopener noreferrer">
                Buy online
              </VenposBuyButton>
            </FeaturedCtaRow>
          </FeaturedBody>
        </Featured>

        <Grid>
          {BOOKS_GRID.map(book => (
            <Card key={book.id}>
              <CoverBand>
                <CoverImage src={book.cover} alt={`Book cover: ${book.title}`} />
              </CoverBand>
              <CardBody>
                <CardTitle>{book.title}</CardTitle>
                {book.subtitle && (
                  <BookSubtitle style={{ marginBottom: '0.75rem' }}>{book.subtitle}</BookSubtitle>
                )}
                <CardText>{book.description}</CardText>
                {book.credit && (
                  <BookSubtitle style={{ marginTop: '1rem', marginBottom: 0, fontSize: '0.9rem' }}>
                    {book.credit}
                  </BookSubtitle>
                )}
                <CtaRow>
                  <VenposBuyButton href={book.venposUrl} target="_blank" rel="noopener noreferrer">
                    Buy online
                  </VenposBuyButton>
                </CtaRow>
              </CardBody>
            </Card>
          ))}
        </Grid>
      </Content>

      <Footer />
    </Page>
  );
};

export default BelvoirBooks;
